import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'es', country: 'CO', label: 'ES' },
  { code: 'en', country: 'US', label: 'EN' },
  { code: 'pt', country: 'BR', label: 'PT' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const langRef = useRef(null);

  const navLinks = [
    { path: '/', label: t('navbar.home') },
    { path: '/smart-treasury', label: t('navbar.smartTreasury') },
    { path: '/#services', label: t('navbar.services'), isAnchor: true },
    { path: '/about', label: t('navbar.about') },
    { path: '/contact', label: t('navbar.contact') },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location]);

  // Close lang dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    if (path === '/#services') {
      return location.pathname === '/' && location.hash === '#services';
    }
    if (path === '/') {
      return location.pathname === '/' && location.hash !== '#services';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleChangeLang = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className={`fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-7xl rounded-full border border-purple-dark/60 bg-gradient-to-r from-core-black/90 via-deep-charcoal/80 to-core-black/90 backdrop-blur-xl flex justify-between items-center px-4 lg:px-8 py-3 z-50 transition-all duration-300 ${scrolled ? 'shadow-[0_4px_30px_rgba(168,67,151,0.2)] border-purple-primary/30' : 'shadow-[0_2px_20px_rgba(0,0,0,0.3)]'}`}>

      <Link to="/" className="flex items-center gap-2 shrink-0" aria-label={t('navbar.aria.logoHome')}>
        <img src="/logo-official.svg" alt="CrossPay Solutions" className="h-8 lg:h-10" />
      </Link>

      {/* Laptop+ horizontal nav (tablet uses hamburger like mobile) */}
      <nav className="hidden lg:flex items-center gap-6 lg:gap-8 font-headline font-medium text-sm tracking-tight" aria-label="Main navigation">
        {navLinks.map(({ path, label, isAnchor }) => (
          isAnchor ? (
            <Link
              key={path}
              to={{ pathname: '/', hash: '#services' }}
              onClick={() => {
                if (location.pathname === '/' && location.hash === '#services') {
                  document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className={`transition-colors duration-300 whitespace-nowrap ${
                isActive(path)
                  ? 'text-accent-magenta font-bold'
                  : 'text-gray-300 hover:text-accent-magenta'
              }`}
            >
              {label}
            </Link>
          ) : (
            <Link
              key={path}
              to={path}
              className={`transition-colors duration-300 whitespace-nowrap ${
                isActive(path)
                  ? 'text-accent-magenta font-bold'
                  : 'text-gray-300 hover:text-accent-magenta'
              }`}
            >
              {label}
            </Link>
          )
        ))}
      </nav>

      <div className="flex items-center gap-3 lg:gap-4 shrink-0">
        {/* Language Selector — solo laptop+ (tablet usa menú hamburguesa) */}
        <div className="relative hidden lg:block" ref={langRef}>
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-2 text-gray-300 hover:text-accent-magenta transition-colors duration-300 px-2 py-1.5 rounded-full border border-transparent hover:border-purple-primary/30"
            aria-label={t('navbar.aria.changeLanguage')}
          >
            <span className="flex h-6 min-w-[1.75rem] items-center justify-center rounded-md bg-white/5 px-1 text-[10px] font-black font-headline leading-none tracking-wide text-on-surface-variant">
              {currentLang.country}
            </span>
            <span className="text-xs font-bold font-headline uppercase leading-none tracking-wider">
              {currentLang.label}
            </span>
            <span className="material-symbols-outlined text-[16px] leading-none">
              {langOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>

          {langOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 min-w-[140px] overflow-hidden rounded-xl border border-purple-dark bg-[#0a090c] shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleChangeLang(lang.code)}
                  className={`flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors duration-200 ${
                    i18n.language === lang.code
                      ? 'text-accent-magenta bg-purple-primary/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className="flex h-6 min-w-[1.75rem] shrink-0 items-center justify-center rounded-md bg-white/5 text-[10px] font-black font-headline leading-none tracking-wide text-on-surface-variant">
                    {lang.country}
                  </span>
                  <span className="text-xs font-bold font-headline uppercase leading-none tracking-wider">
                    {lang.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <a
          href="https://compliance.crosspaysolutions.app/login"
          className="hidden lg:block bg-gradient-to-r from-purple-primary to-accent-magenta text-white px-5 lg:px-6 py-2 rounded-full font-headline text-sm font-bold hover:scale-105 active:scale-95 transition-all duration-300 hover:shadow-[0_0_20px_rgba(168,67,151,0.4)]"
        >
          {t('navbar.login')}
        </a>
        <button
          className="text-white lg:hidden"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? t('navbar.aria.closeMenu') : t('navbar.aria.openMenu')}
          aria-expanded={isOpen}
        >
          <span className="material-symbols-outlined text-2xl">
            {isOpen ? 'close' : 'menu'}
          </span>
        </button>
      </div>

      {/* Mobile + tablet menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl border border-purple-dark bg-[#0a090c] p-6 shadow-[0_12px_40px_rgba(0,0,0,0.55)] flex flex-col gap-4 lg:hidden">
          {navLinks.map(({ path, label, isAnchor }) => (
            isAnchor ? (
              <Link
                key={path}
                to={{ pathname: '/', hash: '#services' }}
                onClick={() => {
                  setIsOpen(false);
                  if (location.pathname === '/' && location.hash === '#services') {
                    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className={`py-2 text-lg font-headline font-medium ${
                  isActive(path) ? 'text-accent-magenta' : 'text-gray-300'
                }`}
              >
                {label}
              </Link>
            ) : (
              <Link
                key={path}
                to={path}
                className={`py-2 text-lg font-headline font-medium ${
                  isActive(path) ? 'text-accent-magenta' : 'text-gray-300'
                }`}
              >
                {label}
              </Link>
            )
          ))}

          {/* Idioma (misma alineación que escritorio) */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-purple-dark/50">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleChangeLang(lang.code)}
                className={`flex items-center gap-2 rounded-full border px-3 py-2 transition-colors duration-200 ${
                  i18n.language === lang.code
                    ? 'border-accent-magenta text-accent-magenta bg-purple-primary/10'
                    : 'border-purple-dark/50 text-gray-400 hover:text-white hover:border-purple-primary/50'
                }`}
              >
                <span className="flex h-6 min-w-[1.75rem] items-center justify-center rounded-md bg-white/5 text-[10px] font-black font-headline leading-none tracking-wide text-on-surface-variant">
                  {lang.country}
                </span>
                <span className="text-xs font-bold font-headline uppercase leading-none tracking-wider">
                  {lang.label}
                </span>
              </button>
            ))}
          </div>

          <a
            href="https://compliance.crosspaysolutions.app/login"
            className="bg-gradient-to-r from-purple-primary to-accent-magenta text-white text-center py-3 rounded-xl font-headline font-bold mt-2"
          >
            {t('navbar.login')}
          </a>
        </div>
      )}
    </header>
  );
}
